/**
 * Geometry utility functions for map calculations
 */

/**
 * Calculate distance between two geographic coordinates using Haversine formula
 * @param coord1 - First coordinate [longitude, latitude]
 * @param coord2 - Second coordinate [longitude, latitude]
 * @returns Distance in meters
 */
export const calculateDistance = (
  coord1: number[],
  coord2: number[]
): number => {
  const [lon1, lat1] = coord1
  const [lon2, lat2] = coord2
  const R = 6371000 // Earth's radius in meters

  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLon = ((lon2 - lon1) * Math.PI) / 180

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2)

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

/**
 * Get midpoint between two geographic coordinates
 * @param coord1 - First coordinate [longitude, latitude]
 * @param coord2 - Second coordinate [longitude, latitude]
 * @returns Midpoint coordinate [longitude, latitude]
 */
export const getMidpoint = (coord1: number[], coord2: number[]): number[] => {
  return [(coord1[0] + coord2[0]) / 2, (coord1[1] + coord2[1]) / 2]
}

/**
 * Extract coordinates from GeoJSON geometry
 * @param geoJson - GeoJSON geometry object
 * @returns Array of coordinate arrays
 */
export const extractCoordinates = (geoJson: {
  type: string
  coordinates: any
}): number[][][] => {
  let coordinates: number[][][] = []
  if (geoJson.type === 'MultiPolygon') {
    coordinates = geoJson.coordinates[0] // Take first polygon of multipolygon
  } else if (geoJson.type === 'Polygon') {
    coordinates = geoJson.coordinates
  }
  return coordinates
}

/**
 * Format distance for display
 * @param meters - Distance in meters
 * @returns Formatted distance string (e.g., "150m" or "1.5km")
 */
export const formatDistance = (meters: number): string => {
  if (!meters) return ''
  if (meters < 1000) return `${Math.round(meters)}m`
  return `${(meters / 1000).toFixed(1)}km`
}
